import type { Explanation, ExplanationNowKind } from './types';

/**
 * Pure rendering of an Explanation — the HTML the tooltip shows and the plain
 * text screen readers and aria-labels get. No Vue, no i18n: the text arrives
 * translated (see types.ts).
 *
 * The HTML goes to PrimeVue's tooltip with `escape: false`, so EVERY piece of
 * text is escaped here. The sentences come from the translations service,
 * which is editable data — never markup we trust.
 */

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

export const escapeHtml = (text: string): string => text.replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);

/** Icon classes are attribute values: keep only what a PrimeIcons class can contain. */
const safeIconClass = (icon: string): string => icon.replace(/[^\w\s-]/g, '');

const NOW_ICONS: Record<ExplanationNowKind, string> = {
  yes: 'pi pi-arrow-right',
  no: 'pi pi-lock',
  current: 'pi pi-map-marker',
  info: 'pi pi-info-circle',
};

/** The class PrimeVue puts on the tooltip root; theme.css styles `.p-tooltip.explanation`. */
export const EXPLANATION_CLASS = 'explanation';

/**
 * The tooltip card, in the fixed order: head (icon + title) → what →
 * calculation → steps → scope → now. `fallbackTitle` is the element's own
 * label, used when the explanation has no title.
 */
export const renderExplanation = (ex: Explanation, fallbackTitle = ''): string => {
  const title = (ex.title ?? fallbackTitle).trim();
  const tone = ex.tone ?? 'neutral';
  const parts: string[] = [];

  if (title || ex.icon) {
    const icon = ex.icon ? `<i class="${safeIconClass(ex.icon)}" aria-hidden="true"></i>` : '';
    const strong = title ? `<strong>${escapeHtml(title)}</strong>` : '';
    parts.push(`<div class="explanation-head explanation-head--${tone}">${icon}${strong}</div>`);
  }

  parts.push(`<p class="explanation-what">${escapeHtml(ex.what)}</p>`);

  if (ex.calculation?.length) {
    const items = ex.calculation.map((term) => `<li>${escapeHtml(term)}</li>`).join('');
    parts.push(`<ul class="explanation-calculation">${items}</ul>`);
  }

  if (ex.steps?.length) {
    const items = ex.steps.map((step) => `<li>${escapeHtml(step)}</li>`).join('');
    parts.push(`<ol class="explanation-steps">${items}</ol>`);
  }

  if (ex.scope) {
    parts.push(
      `<p class="explanation-scope"><i class="pi pi-filter" aria-hidden="true"></i><span>${escapeHtml(ex.scope)}</span></p>`,
    );
  }

  if (ex.now) {
    parts.push(
      `<div class="explanation-now explanation-now--${ex.now.kind}">`
      + `<i class="${NOW_ICONS[ex.now.kind]}" aria-hidden="true"></i>`
      + `<span>${escapeHtml(ex.now.text)}</span></div>`,
    );
  }

  return parts.join('');
};

/**
 * The same explanation as one plain sentence run — for `aria-label` /
 * `aria-describedby` and for tests that read text. Same order as the card.
 */
export const explanationToText = (ex: Explanation, fallbackTitle = ''): string => {
  const title = (ex.title ?? fallbackTitle).trim();

  return [
    title,
    ex.what,
    ...(ex.calculation ?? []),
    ...(ex.steps ?? []),
    ex.scope ?? '',
    ex.now?.text ?? '',
  ]
    .map((s) => s.trim())
    .filter(Boolean)
    .join(' — ');
};

/** The legacy `headerTooltip` string, read as an Explanation (the alias). */
export const explanationFromText = (text: string | null | undefined): Explanation | null =>
  text && text.trim() ? { what: text } : null;
